<script>
  import { _ } from "svelte-i18n";
  import { onMount } from "svelte";
  import { currentPage } from "../../utilities/DataStore";
  import { BASE_URL } from "../../utilities/config.js";
  import { sanitizeAssessmentHtml } from "../../utilities/TextSanitizer.js";
  import Loader from "./report/Loader.svelte";
  import IeltsReportHeader from "./report/IeltsReportHeader.svelte";
  import IeltsReportItemHeader from "./report/IeltsReportItemHeader.svelte";
  import { buildIeltsWritingReport } from "../ieltsSkills.mjs";
  import { formatIeltsBand } from "../ieltsReading.mjs";

  export let instanceId;
  export let instanceData;
  // "SCORE" shows the band and per-task summary only, "DETAIL" adds the
  // examiner comments and the improved version of each answer.
  export let variant = "SCORE";

  let reportStatus = "LOADING";
  let report = { tasks: [], band: null, totalWords: 0 };

  // A writing paper can be scored — band 0 — without a single word written, so
  // the header needs to know the difference between a weak answer and no
  // answer at all before it describes what the band means. Only the tasks
  // themselves can say that: with no task data there is nothing to have
  // attempted, and the header keeps its band-based wording.
  $: attempted =
    report.tasks.length === 0 || report.tasks.some((task) => task.answered);

  let collapsedTasks = {};

  async function loadDataProgress() {
    reportStatus = "LOADING";
    return fetch(
      BASE_URL +
        `/public/assessment/instance/` +
        instanceId +
        `/progress-report`,
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((data) => {
        report = buildIeltsWritingReport(data);
        reportStatus = "OK";
      })
      .catch((error) => {
        console.error("catch error", error);
        reportStatus = "ERROR";
      });
  }

  onMount(() => {
    loadDataProgress();
  });

  function toggleTask(taskNumber) {
    collapsedTasks = {
      ...collapsedTasks,
      [taskNumber]: !collapsedTasks[taskNumber],
    };
  }
</script>

<div class="container ielts-report paper-shadow">
  {#if reportStatus === "LOADING"}
    <Loader />
  {:else if reportStatus === "ERROR"}
    <div class="text-center p-5">{$_("ERROR_LOADING_ASSESSMENT")}</div>
  {:else}
    <IeltsReportHeader
      {instanceData}
      band={report.band}
      skill="WRITING"
      {attempted}
    />

    {#if variant !== "DETAIL"}
      <div class="mb-4 mt-5">
        <hr data-content={$_("SCORE_SUMMARY")} class="hr-text" />
      </div>

      <div class="row justify-content-center">
        {#each report.tasks as task (task.number)}
          <div class="col-md-4 col-6 text-center summary-box">
            <div class="summary-value">{formatIeltsBand(task.band)}</div>
            <div class="summary-label">{task.title}</div>
          </div>
        {/each}
        <div class="col-md-4 col-6 text-center summary-box">
          <div class="summary-value">{report.totalWords}</div>
          <div class="summary-label">{$_("WORD_COUNT")}</div>
        </div>
      </div>
    {/if}

    {#if variant === "DETAIL"}
      <div class="mb-2 mt-5">
        <h4 class="section-title">{$_("DETAILED_FEEDBACK")}</h4>
        <hr />
      </div>

      {#each report.tasks as task (task.number)}
        <div class="mb-4">
          <IeltsReportItemHeader
            number={task.number}
            title={task.title}
            value={task.band === null ? null : formatIeltsBand(task.band)}
            label={$_("BAND")}
            emptyText={$_("NOT_ANSWERED")}
            collapsed={collapsedTasks[task.number]}
            toggle={() => toggleTask(task.number)}
          />

          <!-- The header spans the full width; the body splits in two: the
               task and what the candidate wrote on the left, the examiner
               comments on the right. -->
          <div
            class="ielts-collapsible-body"
            class:collapsed={collapsedTasks[task.number]}
          >
            <div class="row mt-3">
              <div class="col-lg-6 mb-3">
                {#if task.prompt}
                  <div class="prompt-box mb-3 ielts-avoid-break">
                    <h6 class="column-title">{$_("TASK")}</h6>
                    <div class="prompt-text">
                      {@html sanitizeAssessmentHtml(task.prompt)}
                    </div>
                  </div>
                {/if}

                <h6 class="column-title mb-0">{$_("YOUR_ANSWER")}</h6>
                <p class="text-muted small mb-2">
                  {$_("WORD_COUNT")}: {task.wordCount ?? 0}{#if task.minWords}&nbsp;<span
                      class:text-danger={task.underMinWords}
                      >(min {task.minWords})</span
                    >{/if}
                </p>
                {#if task.answered}
                  <div class="answer-box">{task.answerText}</div>
                {:else}
                  <div class="text-muted">{$_("NOT_ANSWERED")}</div>
                {/if}
              </div>

              <div class="col-lg-6 mb-3">
                {#if task.criteria.length}
                  <h6 class="column-title mb-0">{$_("EXAMINER_COMMENTS")}</h6>
                  <p class="text-muted small mb-2">
                    {$_("EXAMINER_COMMENTS_DESCRIPTION")}
                  </p>
                  {#each task.criteria as criterion (criterion.key)}
                    <div class="criterion ielts-avoid-break">
                      <div class="criterion-header">
                        <span>{$_("IELTS_WRITING_" + criterion.key)}</span>
                        {#if criterion.band !== null}
                          <span class="criterion-band"
                            >{formatIeltsBand(criterion.band)}</span
                          >
                        {/if}
                      </div>
                      {#if criterion.feedback}
                        <div class="criterion-feedback">
                          {criterion.feedback}
                        </div>
                      {/if}
                    </div>
                  {/each}
                {/if}
              </div>
            </div>

            <!-- Full width below the two columns: it is a long piece of prose
                 that reads badly in half a column. -->
            {#if task.improvedVersion}
              <div class="improved-box ielts-avoid-break">
                <div class="improved-label">
                  {$_("IMPROVED_VERSION")}{#if task.improvedVersionBand !== null}
                    &nbsp;— {$_("BAND")}
                    {formatIeltsBand(task.improvedVersionBand)}{/if}
                </div>
                <p class="text-muted small mb-2">
                  {$_("IMPROVED_VERSION_DESCRIPTION")}
                </p>
                <div class="improved-text">{task.improvedVersion}</div>
              </div>
            {/if}
          </div>
        </div>
      {/each}
    {/if}

    <div class="row justify-content-center mt-4 report-footer-link">
      <a
        href="/"
        on:click={() => {
          $currentPage = "";
        }}>assessment.oksensay.com</a
      >
    </div>
  {/if}
</div>

<style>
  @media (max-width: 768px) {
    .container {
      padding: 0;
    }
  }

  .summary-box {
    padding: 1rem;
  }

  .summary-value {
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }

  .summary-label {
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #9ca3af;
  }

  hr.hr-text {
    position: relative;
    border: none;
    height: 1px;
    background: #999;
  }

  hr.hr-text::before {
    content: attr(data-content);
    display: inline-block;
    background: #fff;
    font-weight: bold;
    font-size: 0.85rem;
    color: #999;
    border-radius: 30rem;
    padding: 0.2rem 2rem;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .section-title {
    font-weight: 700;
    color: #1f2937;
  }

  .column-title {
    font-weight: 700;
    color: #374151;
  }

  .prompt-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1rem 1.25rem;
  }

  .prompt-text {
    font-size: 0.875rem;
    color: #4b5563;
    line-height: 1.6;
  }

  /* No max-height/overflow here on purpose: the answer always prints and
     reads in full rather than inside its own scroll area. */
  .answer-box {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    padding: 1.25rem;
    font-size: 0.875rem;
    color: #1f2937;
    line-height: 1.7;
    white-space: pre-wrap;
    height: auto;
    max-height: none;
    overflow-y: visible;
  }

  .criterion {
    border: 1px solid #e5e7eb;
    border-radius: 0.375rem;
    padding: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .criterion-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #374151;
  }

  .criterion-band {
    font-size: 1rem;
    font-weight: 700;
    color: #0056b3;
  }

  .criterion-feedback {
    margin-top: 0.4rem;
    font-size: 0.8rem;
    color: #4b5563;
    line-height: 1.6;
  }

  .improved-box {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 0.5rem;
    padding: 1.25rem;
    margin-top: 0.5rem;
  }

  .improved-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-size: 0.75rem;
    color: #166534;
  }

  .improved-text {
    font-size: 0.875rem;
    color: #14532d;
    line-height: 1.7;
    white-space: pre-wrap;
  }

  /* Wider than Bootstrap's .container cap, with tighter side padding, so the
     report uses the width a full-HD screen actually has. */
  .paper-shadow {
    position: relative;
    background: #fff;
    max-width: 1500px;
    padding: 3rem 2rem;
    margin: 50px auto;
    box-shadow: 0px 2px 38px rgba(0, 0, 0, 0.2);
  }
  .paper-shadow:after,
  .paper-shadow:before {
    content: "";
    position: absolute;
    left: auto;
    background: none;
    z-index: -1;
  }
  .paper-shadow:after {
    width: 90%;
    height: 10px;
    top: 30px;
    right: 8px;
    transform: rotate(-3deg);
    box-shadow: 0px -25px 35px 0px rgba(0, 0, 0, 0.5);
  }

  .paper-shadow:before {
    width: 10px;
    height: 95%;
    top: 5px;
    right: 18px;
    transform: rotate(3deg);
    box-shadow: 22px 0px 35px 0px rgba(0, 0, 0, 0.5);
  }
</style>
